import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatCurrency } from "../../../lib/format";
import { Avatar, memberFillClass, Stack } from "../../ui";
import type { Member } from "../types";
import { labelForMember } from "../utils";

interface MemberBalanceListProps {
  stats: Array<{
    member: Member;
    paid: number;
    paidShared: number;
    paidPrivate: number;
  }>;
  members: Member[];
  currency: string;
}

/**
 * "Hvem har lagt ut hva" — per-member horizontal bar showing how much
 * each member has paid, split into private vs shared portions.
 *
 * Shared uses a dark universal color so it never blends with the bar
 * background; private uses the member's avatar identity color so each
 * person's private spending is visually tied to them. Total amount on
 * the right is paidShared + paidPrivate.
 */
export function MemberBalanceList({ stats, members, currency }: MemberBalanceListProps) {
  const max = Math.max(...stats.map((s) => s.paid), 0);
  return (
    <Stack gap={4}>
      {stats.map((s) => {
        const lbl = labelForMember(s.member, members);
        const sharedFraction = max === 0 ? 0 : s.paidShared / max;
        const privateFraction = max === 0 ? 0 : s.paidPrivate / max;
        const hasShared = s.paidShared > 0.005;
        const hasPrivate = s.paidPrivate > 0.005;
        const privateClass = memberFillClass(lbl.name);
        return (
          <div key={s.member.userId}>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Avatar name={s.member.displayName ?? s.member.email ?? "?"} size="sm" />
                <span className="truncate text-ink-800 text-sm" title={lbl.tooltip}>
                  {lbl.name}
                </span>
              </div>
              <span className="tabular shrink-0 font-medium text-ink-900 text-sm">
                {formatCurrency(s.paid, currency)}
              </span>
            </div>
            <div className="flex h-2 overflow-hidden rounded-full bg-ink-100">
              {hasPrivate && (
                <div
                  className={cn("h-full transition-all", privateClass)}
                  style={{ width: `${Math.max(privateFraction * 100, 4)}%` }}
                  title={t.settlement.charts.breakdownPrivate(
                    formatCurrency(s.paidPrivate, currency),
                  )}
                />
              )}
              {hasShared && (
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.max(sharedFraction * 100, 4)}%` }}
                  title={t.settlement.charts.breakdownPaidShared(
                    formatCurrency(s.paidShared, currency),
                  )}
                />
              )}
            </div>
            <div className="tabular mt-1.5 flex justify-end gap-3 text-ink-500 text-xs">
              {hasPrivate && (
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className={cn("h-2 w-2 rounded-sm", privateClass)} />
                  {t.settlement.charts.breakdownPrivate(formatCurrency(s.paidPrivate, currency))}
                </span>
              )}
              {hasShared && (
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className="h-2 w-2 rounded-sm bg-primary" />
                  {t.settlement.charts.breakdownPaidShared(formatCurrency(s.paidShared, currency))}
                </span>
              )}
            </div>
          </div>
        );
      })}
      <p className="mt-2 border-ink-100 border-t pt-4 text-ink-500 text-xs">
        {t.settlement.charts.perMemberFootnote}
      </p>
    </Stack>
  );
}
